import type { ModerationActionType, ModerationCapabilityState } from '../api/types.js';

/**
 * How moderation vocabulary is said to an operator (§65, §98.6).
 *
 * ONE mapping, shared by the finding detail panel and the capability panel.
 * They previously would have carried their own, and two translations of
 * `REAUTH_REQUIRED` that drift apart is how a customer ends up told to
 * reconnect on one screen and that the platform is unsupported on another.
 */

export const ACTION_LABEL: Record<ModerationActionType, string> = {
  'message.delete': 'Delete message',
  'user.timeout': 'Time out',
  'user.ban': 'Ban',
  'user.unban': 'Unban',
};

/**
 * Each state in the operator's terms, and — the part that matters — WHOSE
 * problem it is.
 *
 * §98.6 keeps these six apart precisely so that a disabled button can say
 * whether reconnecting fixes it, whether Connaro owes them work, or whether it
 * can never work. Collapsing them here would undo that at the last step.
 */
export const CAPABILITY_LABEL: Record<ModerationCapabilityState, string> = {
  SUPPORTED: 'Available',
  /*
   * Two words, and the distinction between them is the whole point.
   *
   *   Unavailable  the platform can do it; Connaro has not built it. A
   *                customer may reasonably wait for a release.
   *   Unsupported  the platform exposes no API for it. Waiting will not help,
   *                and saying "not built yet" would promise something that
   *                cannot be written.
   *
   * `capability-honesty.test.ts` holds every adapter to that line.
   */
  UNSUPPORTED: 'Unsupported',
  NOT_IMPLEMENTED: 'Unavailable',
  PERMISSION_REQUIRED: 'Extra permission needed',
  REAUTH_REQUIRED: 'Reconnect required',
  PROVIDER_REVIEW_REQUIRED: 'Awaiting platform review',
};

/** Whose problem the state is, for grouping and tone. */
export function isOurGap(state: ModerationCapabilityState): boolean {
  return state === 'NOT_IMPLEMENTED';
}

/** True when the customer can fix it themselves, and should be told how. */
export function isCustomerFixable(state: ModerationCapabilityState): boolean {
  return state === 'REAUTH_REQUIRED' || state === 'PERMISSION_REQUIRED';
}
